import React, { useContext, useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  useTheme,
  Chip
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OptimizedImage from './OptimizedImage';
import logoImage from '../assets/playmitos-text-logo.png';

function Navbar() {
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '192.168.1.7' ||
                       window.location.hostname.startsWith('192.168.');

  useEffect(() => {
    if (isDevelopment) {
      document.title = 'PlayMitos - QA';
    }
  }, [isDevelopment]);

  if (!user || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  const handleMenuOpen = (event) => {
    event.preventDefault();
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.style.overflow = 'hidden';
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
    setAnchorEl(null);

    setTimeout(() => {
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('overflow');
      window.getSelection()?.removeAllRanges();
    }, 100);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      console.log('Iniciando logout desde Navbar');
      await logout();
      console.log('Logout exitoso');
      handleMenuClose();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error durante el logout:', error);
    }
  };

  const handleMatchClick = (matchId) => {
    handleMenuClose();
    setTimeout(() => {
      navigate(`/match/${matchId}`);
    }, 100);
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        width: '100%',
        top: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '& .MuiToolbar-root': {
          minHeight: '64px',
          height: '64px',
          '@media (min-width: 600px)': {
            minHeight: '64px',
            height: '64px'
          }
        }
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={handleMenuOpen}
          sx={{ 
            mr: 0.5,
            color: 'text.primary',
            '& .MuiSvgIcon-root': {
              fontSize: '1.8rem'
            }
          }}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              width: 198,
              maxWidth: '100%',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            },
            '& .MuiListItemIcon-root': {
              minWidth: '36px',
              '& .MuiSvgIcon-root': {
                fontSize: '1.35rem',
                color: 'text.primary'
              }
            },
            '& .MuiListItemText-primary': {
              fontSize: '0.855rem',
              color: 'text.primary'
            }
          }}
        >
          <MenuItem onClick={() => handleMenuClick('/')}>
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText>Inicio</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick('/help')}>
            <ListItemIcon>
              <HelpIcon />
            </ListItemIcon>
            <ListItemText>Ayuda</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick('/new-match')}>
            <ListItemIcon>
              <AddCircleIcon />
            </ListItemIcon>
            <ListItemText>Nueva Partida</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick('/my-matches')}>
            <ListItemIcon>
              <SportsEsportsIcon />
            </ListItemIcon>
            <ListItemText>Mis Partidas</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick('/decks')}>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText>Mazos</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick('/profile')}>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText>Perfil</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={handleLogout}
            sx={{
              '& .MuiTypography-root': {
                color: 'error.main'
              }
            }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Cerrar Sesión</ListItemText>
          </MenuItem>
        </Menu>

        <Box 
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            height: '64px',
            justifyContent: 'center',
            ml: -3,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                height: '64px'
              }}
            >
              <OptimizedImage
                src={logoImage}
                alt="PlayMitos Logo"
                width="150px"
                height="auto"
                sx={{
                  height: '150px',
                  width: '180px',
                  objectFit: 'contain',
                  position: 'relative',
                  '@keyframes bounce': {
                    '0%, 100%': {
                      transform: 'translate(0px, 0px)'
                    },
                    '25%': {
                      transform: 'translate(0px, -3px)'
                    },
                    '50%': {
                      transform: 'translate(0px, 0px)'
                    },
                    '75%': {
                      transform: 'translate(0px, -2px)'
                    }
                  },
                  '&:hover': {
                    animation: 'bounce 1s ease-in-out'
                  }
                }}
              />
              <Typography 
                component="span"
                sx={{ 
                  fontSize: '0.63rem',
                  color: 'text.secondary',
                  position: 'absolute',
                  right: '25px',
                  bottom: '5px',
                  letterSpacing: '0.9px',
                  fontWeight: 500
                }}
              >
                BETA
              </Typography>
            </Box>
          </Box>
        </Box>
        {isDevelopment && (
          <Chip
            label="QA"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '60%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'error.main',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              height: '32px',
              '& .MuiChip-label': {
                px: 2
              }
            }}
          />
        )}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
              onClick={() => navigate('/help')}
            >
              <IconButton
                sx={{
                  color: 'text.primary',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <HelpIcon />
              </IconButton>
              <Typography 
                sx={{ 
                  fontSize: '0.75rem',
                  marginTop: '1px',
                  color: 'text.primary'
                }}
              >
                Ayuda
              </Typography>
            </Box>
            <Box
              component={Link}
              to="/profile"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    border: '2px solid white',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                {user.username.charAt(0).toUpperCase()}
              </Avatar>
              <Typography 
                sx={{ 
                  fontSize: '0.75rem',
                  marginTop: '2px',
                  color: 'text.primary'
                }}
              >
                {user.username}
              </Typography>
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;